import Icon from 'components/Icon';
import TextoComDestaque from 'components/TextoComDestaque';

/**
 * Uma pergunta do acordeão, usada na página de FAQ e no bloco de destaque da Home.
 *
 * Segue o padrão de disclosure do WAI-ARIA (botão com aria-expanded + aria-controls) em vez
 * de <details>, porque o estado precisa ser controlado de fora: a página abre uma pergunta
 * específica quando ela chega pela âncora da URL.
 *
 * O `id` fica no <section> para que /perguntas-frequentes#slug role até aqui; o scroll-mt-24
 * evita que a NavBar fixa cubra o título ao chegar pela âncora.
 *
 * A resposta fechada continua no DOM, só com `hidden`. Antes ela era montada apenas ao abrir, e o
 * Google — que indexa o HTML renderizado e não clica em nada — via as perguntas sem resposta, na
 * página do FAQ e no bloco da Home. `hidden` é display:none, então o leitor de tela também não a
 * lê fechada, e a animação de entrada roda de novo a cada abertura.
 */
export default function ItemPergunta({ item, aberta, onAlternar }) {
  return (
    <section
      id={item.id}
      className="scroll-mt-24 overflow-hidden rounded-2xl border-caixa border-border bg-background"
    >
      <h3>
        <button
          type="button"
          onClick={() => onAlternar(item.id)}
          aria-expanded={aberta}
          aria-controls={`resposta-${item.id}`}
          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary sm:px-6 sm:py-5"
        >
          <span className="text-base font-semibold text-dark sm:text-lg">{item.pergunta}</span>
          <Icon
            name="keyboard_arrow_down"
            className={`h-4 w-4 flex-none text-primary transition-transform duration-200 ${
              aberta ? 'rotate-180' : ''
            }`}
          />
        </button>
      </h3>

      <div
        id={`resposta-${item.id}`}
        hidden={!aberta}
        className="animate-fade-in border-t border-border bg-white px-5 py-5 sm:px-6"
      >
        {item.resposta.map((paragrafo) => (
          <p
            key={paragrafo}
            className="mt-3 t-corpo first:mt-0"
          >
            <TextoComDestaque texto={paragrafo} />
          </p>
        ))}

        {item.itens?.length ? (
          <ol className="mt-4 space-y-3 t-corpo">
            {item.itens.map((passo, indice) => (
              <li key={passo} className="flex gap-3">
                <span className="mt-1 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {indice + 1}
                </span>
                <span><TextoComDestaque texto={passo} /></span>
              </li>
            ))}
          </ol>
        ) : null}

        {item.respostaFinal?.map((paragrafo) => (
          <p key={paragrafo} className="mt-4 t-corpo">
            <TextoComDestaque texto={paragrafo} />
          </p>
        ))}
      </div>
    </section>
  );
}
